import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Download,
  Folder,
  InsertDriveFile,
  Visibility,
} from "@mui/icons-material";
import { formatBytes } from "components/preview/previewTypes";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";
import WorkspaceFileViewer, { type ViewerFile } from "./WorkspaceFileViewer";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import {
  useGetPdfJobFileUrlMutation,
  useGetPdfJobFilesQuery,
  useLazyGetPdfJobFilesQuery,
  type PdfWorkspaceItem,
  type PdfWorkspaceSection,
} from "features/pdfTools/pdfTools";

const SECTION_TABS: { key: PdfWorkspaceSection; label: string; hint: string }[] = [
  {
    key: "input",
    label: "Input PDFs",
    hint: "Source PDFs the tool extracted from the uploaded archive.",
  },
  {
    key: "result",
    label: "Result",
    hint: "One folder per employee with the renamed files.",
  },
  {
    key: "error_pages",
    label: "Error pages",
    hint: "Pages the tool could not match to an employee (missing name, SSN or year). They are not included in any employee folder — review them manually.",
  },
];

interface JobWorkspacePanelProps {
  jobId: string;
  /** Refresh listings periodically while the job is running. */
  live: boolean;
}

export default function JobWorkspacePanel({
  jobId,
  live,
}: JobWorkspacePanelProps) {
  const dispatch = useAppDispatch();
  const [section, setSection] = useState<PdfWorkspaceSection>("result");
  const [prefix, setPrefix] = useState("");
  const [extraItems, setExtraItems] = useState<PdfWorkspaceItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const {
    data: firstPage,
    isFetching,
    refetch,
  } = useGetPdfJobFilesQuery(
    { jobId, section, prefix, limit: 100 },
    { pollingInterval: live ? 8000 : 0, refetchOnMountOrArgChange: true },
  );
  const [fetchMore, { isFetching: fetchingMore }] = useLazyGetPdfJobFilesQuery();
  const [getFileUrl] = useGetPdfJobFileUrlMutation();

  useEffect(() => {
    setExtraItems([]);
    setNextCursor(firstPage?.next_cursor ?? null);
  }, [firstPage]);

  useEffect(() => {
    setPrefix("");
    setExtraItems([]);
    setNextCursor(null);
  }, [section]);

  const items = useMemo(
    () => [...(firstPage?.items ?? []), ...extraItems],
    [firstPage, extraItems],
  );

  const viewerFiles: ViewerFile[] = useMemo(
    () =>
      items
        .filter(
          (item): item is PdfWorkspaceItem & { key: string } =>
            item.type === "file" &&
            !!item.key &&
            item.name.toLowerCase().endsWith(".pdf"),
        )
        .map((item) => ({ name: item.name, key: item.key, size: item.size })),
    [items],
  );

  useEffect(() => {
    setViewerIndex(null);
  }, [section, prefix]);

  const crumbs = prefix.split("/").filter(Boolean);
  const sectionMeta = SECTION_TABS.find((t) => t.key === section);

  const handleLoadMore = async () => {
    if (!nextCursor) return;
    try {
      const res = await fetchMore({
        jobId,
        section,
        prefix,
        cursor: nextCursor,
        limit: 100,
      }).unwrap();
      setExtraItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.next_cursor);
    } catch {
      dispatch(
        pushNotification({
          message: "Could not load more files. Try again.",
          severity: "error",
        }),
      );
    }
  };

  const handlePreview = (item: PdfWorkspaceItem) => {
    const idx = viewerFiles.findIndex((f) => f.key === item.key);
    if (idx >= 0) setViewerIndex(idx);
  };

  const handleDownload = async (item: PdfWorkspaceItem) => {
    if (!item.key) return;
    setBusyKey(item.key);
    try {
      const { url } = await getFileUrl({ jobId, key: item.key }).unwrap();
      startDownloadViaHiddenFrame(url);
    } catch {
      dispatch(
        pushNotification({
          message: `Could not download "${item.name}".`,
          severity: "error",
        }),
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Tabs
        value={section}
        onChange={(_e, v) => setSection(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
      >
        {SECTION_TABS.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {sectionMeta && (
        <Typography variant="caption" color="text.secondary">
          {sectionMeta.hint}
          {live ? " The list refreshes automatically while the job runs." : ""}
        </Typography>
      )}

      <Breadcrumbs sx={{ fontSize: 13 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setPrefix("")}
          sx={{ fontSize: 13 }}
        >
          {sectionMeta?.label ?? section}
        </Link>
        {crumbs.map((part, idx) => {
          const target = `${crumbs.slice(0, idx + 1).join("/")}/`;
          const isLast = idx === crumbs.length - 1;
          return isLast ? (
            <Typography key={target} sx={{ fontSize: 13 }} color="text.primary">
              {part}
            </Typography>
          ) : (
            <Link
              key={target}
              component="button"
              type="button"
              underline="hover"
              onClick={() => setPrefix(target)}
              sx={{ fontSize: 13 }}
            >
              {part}
            </Link>
          );
        })}
      </Breadcrumbs>

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "auto",
          height: { xs: 320, sm: 400, md: 440 },
          bgcolor: "#fff",
        }}
      >
        {isFetching && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : items.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            {live ? "Nothing here yet — files appear as they are produced." : "No files."}
          </Typography>
        ) : (
          items.map((item) => (
            <Box
              key={`${item.type}-${item.path}`}
              onClick={
                item.type === "folder" ? () => setPrefix(item.path) : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.6,
                borderBottom: "1px solid",
                borderColor: "rgba(0,0,0,0.05)",
                cursor: item.type === "folder" ? "pointer" : "default",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {item.type === "folder" ? (
                <Folder sx={{ color: "#f5b942", flexShrink: 0 }} />
              ) : (
                <InsertDriveFile
                  sx={{ color: "text.secondary", flexShrink: 0 }}
                  fontSize="small"
                />
              )}
              <Typography noWrap sx={{ flex: 1, fontSize: 14 }}>
                {item.name}
              </Typography>
              {item.type === "file" && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {formatBytes(item.size)}
                  </Typography>
                  {busyKey === item.key ? (
                    <CircularProgress size={16} sx={{ mx: 1 }} />
                  ) : (
                    <>
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(item)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => void handleDownload(item)}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </Box>
          ))
        )}
        {nextCursor && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Button
              size="small"
              onClick={() => void handleLoadMore()}
              disabled={fetchingMore}
              startIcon={
                fetchingMore ? <CircularProgress size={14} /> : undefined
              }
            >
              Load more
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" onClick={() => void refetch()}>
          Refresh
        </Button>
      </Box>

      {viewerIndex !== null && viewerFiles[viewerIndex] && (
        <WorkspaceFileViewer
          jobId={jobId}
          files={viewerFiles}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onIndexChange={setViewerIndex}
        />
      )}
    </Box>
  );
}
